import React, { useCallback, useState, useEffect, useRef } from 'react'
import { Box, CircularProgress } from '@mui/material'
import InspectorLayout from './components/InspectorLayout'

export interface X12CanonicalSpecProps {
  data: any
  onChange: (data: any) => void
  readonly?: boolean
}

// Simplified component without context
const X12CanonicalSpec = React.memo<X12CanonicalSpecProps>(
  ({ data, onChange, readonly = false }) => {
    // STATE A: Initial data that never changes - passed to children for local state initialization
    const [initialData, setInitialData] = useState(data)
    const [isReady, setIsReady] = useState(false)

    // Update initial data when data prop changes (only happens once or when data source changes)
    useEffect(() => {
      setInitialData(data)
    }, [data])

    // STATE B: Merged data from all children - used only for saving, not attached to UI
    const mergedDataRef = useRef({
      ...data,
      loops: data.loops || [],
    })

    // Update mergedDataRef when initialData changes
    useEffect(() => {
      mergedDataRef.current = {
        ...initialData,
        loops: initialData.loops || [],
      }
    }, [initialData])

    // Defer rendering to prevent UI freezing
    useEffect(() => {
      const timer = setTimeout(() => {
        setIsReady(true)
      }, 0)
      return () => {
        clearTimeout(timer)
      }
    }, [])

    // Simple ref-based data tracking - NO STATE UPDATES
    const lastSavedDataRef = useRef<string>('')

    // Handler for InspectorLayout changes - updates mergedDataRef
    const handleInspectorChange = useCallback((updatedData: any) => {
      // Debug logging
      console.log('InspectorLayout onChange called with:', updatedData)

      // Update the merged data reference with the complete updated data structure
      mergedDataRef.current = updatedData

      console.log('Updated mergedDataRef:', mergedDataRef.current)
    }, [])

    // Simple polling for auto-save - NO DEPENDENCIES
    useEffect(() => {
      const intervalId = setInterval(() => {
        const currentMergedData = JSON.stringify(mergedDataRef.current)

        if (
          lastSavedDataRef.current !== currentMergedData &&
          Object.keys(mergedDataRef.current).length > 0
        ) {
          onChange(mergedDataRef.current)
          lastSavedDataRef.current = currentMergedData
        }
      }, 300) // Check every 300ms

      return () => clearInterval(intervalId)
    }, []) // NO DEPENDENCIES = NO RE-RENDERS

    // Show loading state if not ready
    if (!isReady) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            minHeight: '400px',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )
    }

    // Render inspector layout with full height and width expansion
    const renderResult = (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <InspectorLayout data={initialData} onChange={handleInspectorChange} readonly={readonly} />
      </Box>
    )

    return renderResult
  }
)

X12CanonicalSpec.displayName = 'X12CanonicalSpec'

export default X12CanonicalSpec
